import { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MagicWand, ArrowsLeftRight, Check } from '@phosphor-icons/react';

import Page from '@/components/page/Page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

import {
  MOCK_PAYMENTS,
  MOCK_OUTSTANDING_INVOICES,
  MOCK_POTENTIAL_MATCHES,
} from '../data/mockARData';

// Bank account configuration
const BANK_ACCOUNTS = [
  {
    id: 'chase-main',
    name: 'Chase Business Checking',
    accountNumber: '****4521',
    // Chase blue octagon logo
    logoColor: '#117ACA',
    logoInitial: 'C',
  },
  {
    id: 'bofa-ops',
    name: 'Bank of America Operations',
    accountNumber: '****8834',
    // BofA red logo
    logoColor: '#E31837',
    logoInitial: 'B',
  },
];

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface MatchLine {
  paymentId: string;
  invoiceId: string;
  confidence: number;
  isMultiInvoice?: boolean;
  isPartialPayment?: boolean;
}

interface RowPosition {
  id: string;
  top: number;
  left: number;
  right: number;
}

function ConfidenceBadge({
  confidence,
  isMultiInvoice,
  isPartialPayment,
}: {
  confidence: number;
  isMultiInvoice?: boolean;
  isPartialPayment?: boolean;
}) {
  if (isPartialPayment) {
    return (
      <Badge className="bg-amber-100 text-amber-700 border-amber-200">
        Partial ({confidence}%)
      </Badge>
    );
  }

  if (isMultiInvoice) {
    return (
      <Badge className="bg-purple-100 text-purple-700 border-purple-200">
        Multi ({confidence}%)
      </Badge>
    );
  }

  const color =
    confidence >= 90
      ? 'bg-green-100 text-green-700 border-green-200'
      : confidence >= 70
        ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
        : 'bg-orange-100 text-orange-700 border-orange-200';

  return <Badge className={color}>{confidence}%</Badge>;
}

function MatchingLines({
  matches,
  paymentPositions,
  invoicePositions,
  containerRef,
}: {
  matches: MatchLine[];
  paymentPositions: Map<string, RowPosition>;
  invoicePositions: Map<string, RowPosition>;
  containerRef: React.RefObject<HTMLDivElement | null>;
}) {
  if (matches.length === 0 || !containerRef.current) return null;

  const containerRect = containerRef.current.getBoundingClientRect();

  return (
    <svg
      className="absolute inset-0 pointer-events-none z-10"
      style={{ width: '100%', height: '100%' }}
    >
      <defs>
        <linearGradient id="matchGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#22c55e" />
        </linearGradient>
      </defs>
      {matches.map((match, index) => {
        const paymentPos = paymentPositions.get(match.paymentId);
        const invoicePos = invoicePositions.get(match.invoiceId);

        if (!paymentPos || !invoicePos) return null;

        const startX = paymentPos.right - containerRect.left;
        const startY = paymentPos.top + 20 - containerRect.top;
        const endX = invoicePos.left - containerRect.left;
        const endY = invoicePos.top + 20 - containerRect.top;

        const controlX1 = startX + (endX - startX) * 0.4;
        const controlX2 = startX + (endX - startX) * 0.6;

        return (
          <motion.path
            key={`${match.paymentId}-${match.invoiceId}`}
            d={`M ${startX} ${startY} C ${controlX1} ${startY}, ${controlX2} ${endY}, ${endX} ${endY}`}
            fill="none"
            stroke="url(#matchGradient)"
            strokeWidth="2"
            strokeDasharray="8 4"
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: index * 0.15 }}
          />
        );
      })}
    </svg>
  );
}

// Scanning animation overlay
function ScanningOverlay({ isVisible }: { isVisible: boolean }) {
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="absolute inset-0 z-20 pointer-events-none overflow-hidden rounded-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Scanning line effect */}
          <motion.div
            className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-blue-500 to-transparent"
            initial={{ top: 0 }}
            animate={{ top: '100%' }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
          {/* Subtle overlay */}
          <motion.div
            className="absolute inset-0 bg-blue-500/5"
            animate={{ opacity: [0.02, 0.08, 0.02] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function ARReconciliationPage() {
  const [payments, setPayments] = useState(MOCK_PAYMENTS);
  const [invoices, setInvoices] = useState(MOCK_OUTSTANDING_INVOICES);
  const [selectedPayments, setSelectedPayments] = useState<Set<string>>(
    new Set(),
  );
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(
    new Set(),
  );
  const [activeMatches, setActiveMatches] = useState<MatchLine[]>([]);
  const [isAutoMatching, setIsAutoMatching] = useState(false);
  const [pendingMatches, setPendingMatches] = useState<
    typeof MOCK_POTENTIAL_MATCHES
  >([]);
  const [draggedPayment, setDraggedPayment] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);

  const [paymentPositions, setPaymentPositions] = useState<
    Map<string, RowPosition>
  >(new Map());
  const [invoicePositions, setInvoicePositions] = useState<
    Map<string, RowPosition>
  >(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const paymentRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
  const invoiceRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  const updatePositions = useCallback(() => {
    const newPaymentPositions = new Map<string, RowPosition>();
    const newInvoicePositions = new Map<string, RowPosition>();

    paymentRefs.current.forEach((el, id) => {
      if (el) {
        const rect = el.getBoundingClientRect();
        newPaymentPositions.set(id, {
          id,
          top: rect.top,
          left: rect.left,
          right: rect.right,
        });
      }
    });

    invoiceRefs.current.forEach((el, id) => {
      if (el) {
        const rect = el.getBoundingClientRect();
        newInvoicePositions.set(id, {
          id,
          top: rect.top,
          left: rect.left,
          right: rect.right,
        });
      }
    });

    setPaymentPositions(newPaymentPositions);
    setInvoicePositions(newInvoicePositions);
  }, []);

  useEffect(() => {
    updatePositions();
    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', updatePositions, true);
    return () => {
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', updatePositions, true);
    };
  }, [updatePositions, payments, invoices]);

  const handleAutoMatch = async () => {
    setIsAutoMatching(true);
    setActiveMatches([]);

    // Simulate scanning animation delay
    await new Promise((resolve) => setTimeout(resolve, 1200));

    // Show matches one by one with smooth animation
    const matchesToShow = MOCK_POTENTIAL_MATCHES.filter(
      (m) =>
        payments.find((p) => p.id === m.paymentId)?.status === 'unmatched' &&
        invoices.find((i) => i.id === m.invoiceId)?.status === 'unpaid',
    );

    for (let i = 0; i < matchesToShow.length; i++) {
      await new Promise((resolve) => setTimeout(resolve, 400));
      const match = matchesToShow[i];
      setActiveMatches((prev) => [
        ...prev,
        {
          paymentId: match.paymentId,
          invoiceId: match.invoiceId,
          confidence: match.confidence,
          isMultiInvoice:
            'isMultiInvoice' in match ? match.isMultiInvoice : undefined,
          isPartialPayment:
            'isPartialPayment' in match ? match.isPartialPayment : undefined,
        },
      ]);
    }

    await new Promise((resolve) => setTimeout(resolve, 300));
    setIsAutoMatching(false);
    setPendingMatches(matchesToShow);
  };

  const handleConfirmMatches = () => {
    setPayments((prev) =>
      prev.map((p) => {
        const match = pendingMatches.find((m) => m.paymentId === p.id);
        if (match) {
          return {
            ...p,
            status: 'matched' as const,
            matchedInvoiceId: match.invoiceId,
          };
        }
        return p;
      }),
    );

    setInvoices((prev) =>
      prev.map((i) => {
        const match = pendingMatches.find((m) => m.invoiceId === i.id);
        if (match) {
          // Check if this is a partial payment match
          const isPartial =
            'isPartialPayment' in match && match.isPartialPayment;
          return {
            ...i,
            status: isPartial ? ('partial' as const) : ('paid' as const),
          };
        }
        return i;
      }),
    );

    setPendingMatches([]);
    setTimeout(() => setActiveMatches([]), 2000);
  };

  const handleDismissMatches = () => {
    setPendingMatches([]);
    setActiveMatches([]);
  };

  const handleDragStart = (paymentId: string) => {
    setDraggedPayment(paymentId);
  };

  const handleDragOver = (invoiceId: string) => {
    setDropTarget(invoiceId);
  };

  const handleDragEnd = () => {
    if (draggedPayment && dropTarget) {
      const payment = payments.find((p) => p.id === draggedPayment);
      const invoice = invoices.find((i) => i.id === dropTarget);

      if (payment && invoice && payment.amount === invoice.amount) {
        setPayments((prev) =>
          prev.map((p) =>
            p.id === draggedPayment
              ? {
                  ...p,
                  status: 'matched' as const,
                  matchedInvoiceId: dropTarget,
                }
              : p,
          ),
        );
        setInvoices((prev) =>
          prev.map((i) =>
            i.id === dropTarget ? { ...i, status: 'paid' as const } : i,
          ),
        );
        setActiveMatches((prev) => [
          ...prev,
          { paymentId: draggedPayment, invoiceId: dropTarget, confidence: 100 },
        ]);
      }
    }

    setDraggedPayment(null);
    setDropTarget(null);
  };

  const togglePaymentSelection = (id: string) => {
    setSelectedPayments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleInvoiceSelection = (id: string) => {
    setSelectedInvoices((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Page title="AR Reconciliation">
      <div className="space-y-4">
        {/* Action Bar */}
        <div className="flex items-center justify-end gap-2">
          <Button
            onClick={handleAutoMatch}
            disabled={isAutoMatching}
            className="gap-2"
          >
            <motion.div
              animate={isAutoMatching ? { rotate: 360 } : { rotate: 0 }}
              transition={{
                duration: 1,
                repeat: isAutoMatching ? Infinity : 0,
                ease: 'linear',
              }}
            >
              <MagicWand className="h-4 w-4" weight="fill" />
            </motion.div>
            Auto Match
          </Button>
          <Button
            variant="outline"
            disabled={
              selectedPayments.size === 0 || selectedInvoices.size === 0
            }
            className="gap-2"
          >
            <ArrowsLeftRight className="h-4 w-4" />
            Match Selected
          </Button>
        </div>

        {/* Two-panel layout */}
        <div className="relative" ref={containerRef}>
          <MatchingLines
            matches={activeMatches}
            paymentPositions={paymentPositions}
            invoicePositions={invoicePositions}
            containerRef={containerRef}
          />

          <div className="grid grid-cols-2 gap-6">
            {/* Bank Account Cards - Left Side */}
            <div className="space-y-4">
              {BANK_ACCOUNTS.map((bank) => {
                const bankPayments = payments.filter(
                  (p) => p.bankAccountId === bank.id,
                );
                const unmatchedCount = bankPayments.filter(
                  (p) => p.status === 'unmatched',
                ).length;

                return (
                  <Card key={bank.id} className="relative overflow-hidden">
                    <ScanningOverlay isVisible={isAutoMatching} />
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div
                          className="h-8 w-8 rounded flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: bank.logoColor }}
                        >
                          {bank.logoInitial}
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-sm font-medium">
                            {bank.name}
                          </CardTitle>
                          <p className="text-xs text-muted-foreground">
                            {bank.accountNumber}
                            {unmatchedCount > 0 && (
                              <span className="ml-2 text-amber-600">
                                {unmatchedCount} unmatched
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-8"></TableHead>
                            <TableHead className="text-xs">Date</TableHead>
                            <TableHead className="text-xs">Reference</TableHead>
                            <TableHead className="text-xs">Type</TableHead>
                            <TableHead className="text-xs text-right">
                              Amount
                            </TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <AnimatePresence>
                            {bankPayments.map((payment) => {
                              const isMatched = payment.status === 'matched';
                              const isSelected = selectedPayments.has(
                                payment.id,
                              );
                              const isDragging = draggedPayment === payment.id;
                              const isBeingMatched = activeMatches.some(
                                (m) => m.paymentId === payment.id,
                              );

                              return (
                                <motion.tr
                                  key={payment.id}
                                  ref={(el) => {
                                    if (el)
                                      paymentRefs.current.set(payment.id, el);
                                  }}
                                  className={cn(
                                    'cursor-grab transition-colors',
                                    isMatched && 'bg-green-50',
                                    isDragging && 'opacity-50',
                                    !isMatched &&
                                      !isDragging &&
                                      'hover:bg-muted/50',
                                  )}
                                  draggable={!isMatched}
                                  onDragStart={() =>
                                    handleDragStart(payment.id)
                                  }
                                  onDragEnd={handleDragEnd}
                                  initial={{ opacity: 1 }}
                                  animate={{
                                    opacity: 1,
                                    scale: isBeingMatched ? 1.02 : 1,
                                    backgroundColor: isBeingMatched
                                      ? 'rgb(219 234 254)'
                                      : undefined,
                                  }}
                                  transition={{ duration: 0.3 }}
                                  layout
                                >
                                  <TableCell>
                                    {!isMatched && (
                                      <Checkbox
                                        checked={isSelected}
                                        onCheckedChange={() =>
                                          togglePaymentSelection(payment.id)
                                        }
                                      />
                                    )}
                                  </TableCell>
                                  <TableCell className="text-sm">
                                    {formatDate(payment.date)}
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">
                                    {payment.reference}
                                  </TableCell>
                                  <TableCell className="capitalize text-sm">
                                    {payment.type}
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-sm">
                                    {formatCurrency(payment.amount)}
                                  </TableCell>
                                  <TableCell>
                                    {isMatched ? (
                                      <Badge className="bg-green-100 text-green-700 gap-1">
                                        <Check className="h-3 w-3" />
                                        Matched
                                      </Badge>
                                    ) : (
                                      <Badge variant="secondary">
                                        Unmatched
                                      </Badge>
                                    )}
                                  </TableCell>
                                </motion.tr>
                              );
                            })}
                          </AnimatePresence>
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Invoices Panel */}
            <Card className="relative overflow-hidden">
              <ScanningOverlay isVisible={isAutoMatching} />
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-medium">
                  Outstanding Invoices
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="text-xs">Invoice #</TableHead>
                      <TableHead className="text-xs">Customer</TableHead>
                      <TableHead className="text-xs text-right">
                        Amount
                      </TableHead>
                      <TableHead className="text-xs">Due Date</TableHead>
                      <TableHead className="text-xs">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <AnimatePresence>
                      {invoices.map((invoice) => {
                        const isPaid = invoice.status === 'paid';
                        const isPartial = invoice.status === 'partial';
                        const isMatched = isPaid || isPartial;
                        const isSelected = selectedInvoices.has(invoice.id);
                        const isDropTargetActive = dropTarget === invoice.id;
                        const potentialMatch = activeMatches.find(
                          (m) => m.invoiceId === invoice.id,
                        );
                        const isBeingMatched = !!potentialMatch;

                        return (
                          <motion.tr
                            key={invoice.id}
                            ref={(el) => {
                              if (el) invoiceRefs.current.set(invoice.id, el);
                            }}
                            className={cn(
                              'transition-colors',
                              isMatched && 'bg-green-50',
                              isDropTargetActive &&
                                'bg-blue-50 ring-2 ring-blue-300',
                              !isMatched &&
                                !isDropTargetActive &&
                                'hover:bg-muted/50',
                            )}
                            onDragOver={(e) => {
                              e.preventDefault();
                              handleDragOver(invoice.id);
                            }}
                            onDragLeave={() => setDropTarget(null)}
                            onDrop={handleDragEnd}
                            initial={{ opacity: 1 }}
                            animate={{
                              opacity: 1,
                              scale: isBeingMatched ? 1.02 : 1,
                              backgroundColor: isBeingMatched
                                ? 'rgb(220 252 231)'
                                : undefined,
                            }}
                            transition={{ duration: 0.3 }}
                            layout
                          >
                            <TableCell>
                              {!isMatched && (
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() =>
                                    toggleInvoiceSelection(invoice.id)
                                  }
                                />
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {invoice.invoiceNumber}
                            </TableCell>
                            <TableCell className="text-sm">
                              {invoice.customerName}
                            </TableCell>
                            <TableCell className="text-right font-medium text-sm">
                              {formatCurrency(invoice.amount)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {formatDate(invoice.dueDate)}
                            </TableCell>
                            <TableCell>
                              {isPaid ? (
                                <Badge className="bg-green-100 text-green-700 gap-1">
                                  <Check className="h-3 w-3" />
                                  Paid
                                </Badge>
                              ) : isPartial ? (
                                <Badge className="bg-amber-100 text-amber-700 gap-1">
                                  <Check className="h-3 w-3" />
                                  Partial
                                </Badge>
                              ) : potentialMatch ? (
                                <ConfidenceBadge
                                  confidence={potentialMatch.confidence}
                                  isMultiInvoice={potentialMatch.isMultiInvoice}
                                  isPartialPayment={
                                    potentialMatch.isPartialPayment
                                  }
                                />
                              ) : (
                                <Badge variant="secondary">Unpaid</Badge>
                              )}
                            </TableCell>
                          </motion.tr>
                        );
                      })}
                    </AnimatePresence>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Confirmation Bar */}
        <AnimatePresence>
          {pendingMatches.length > 0 && !isAutoMatching && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="border-green-200 bg-green-50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Check className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="font-medium text-green-900">
                          Found {pendingMatches.length} matches
                        </p>
                        <p className="text-sm text-green-700">
                          Total:{' '}
                          {formatCurrency(
                            pendingMatches.reduce((sum, m) => {
                              const payment = payments.find(
                                (p) => p.id === m.paymentId,
                              );
                              return sum + (payment?.amount || 0);
                            }, 0),
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDismissMatches}
                        className="text-green-700 hover:text-green-900 hover:bg-green-100"
                      >
                        Dismiss
                      </Button>
                      <Button
                        onClick={handleConfirmMatches}
                        className="gap-2 bg-green-600 hover:bg-green-700"
                      >
                        <Check className="h-4 w-4" />
                        Confirm All Matches
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Page>
  );
}

export default ARReconciliationPage;
